// @flow
import React, { Component, Fragment } from 'react';
import { FaRegMinusSquare, FaRegPlusSquare } from 'react-icons/fa';
import BrandCommentInput from 'components/brand/BrandCommentInput/BrandCommentInput';
import type { Comment, SubCommentsMap } from 'store/modules/brands';
import { Link } from 'react-router-dom';
import { fromNow } from 'lib/common';
import MarkdownRender from 'components/common/MarkdownRender';

import cx from 'classnames';

import './BrandComment.scss';

type Props = {
  username: ?string,
  thumbnail: ?string,
  comment: ?string,
  repliesCount: number,
  level: number,
  id: string,
  date: string,
  replies: ?(Comment[]),
  subCommentsMap: SubCommentsMap,
  onReply: (text: string, replyTo: ?string, parentId?: ?string) => Promise<*>,
  onReadReplies: (commentId: string, parentId?: ?string) => Promise<*>,
  logged: boolean,
  currentUsername: ?string,
  onOpenRemove: (payload: { commentId: string, parentId: ?string }) => any,
  parentId?: ?string,
  onEditComment: ({
    commentId: string,
    parentId: ?string,
    text: string,
  }) => any,
};

type State = {
  open: boolean,
  showInput: boolean,
  editing: boolean,
};

class BrandComment extends Component<Props, State> {
  static defaultProps = {
    repliesCount: 0,
    level: 0,
  };

  state = {
    open: false,
    showInput: false,
    editing: false,
  };

  onOpen = () => {
    this.readReplies();
    this.setState({
      open: true,
      showInput: false,
    });
  };

  onClose = () => {
    this.setState({
      open: false,
    });
  };

  onShowInput = () => {
    this.setState({
      showInput: true,
    });
  };

  onHideInput = () => {
    this.setState({
      showInput: false,
    });
  };

  onToggleEdit = () => {
    this.setState({
      editing: !this.state.editing,
    });
  };

  readReplies = () => {
    const { onReadReplies, id, parentId } = this.props;
    onReadReplies(id, parentId);
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const compare = key => {
      return nextProps[key] !== this.props[key];
    };

    return (
      this.state !== nextState ||
      compare('replies') ||
      compare('comment') ||
      compare('repliesCount') ||
      compare('subCommentsMap')
    );
  }

  onOpenRemove = () => {
    const { id, parentId } = this.props;
    this.props.onOpenRemove({
      commentId: id,
      parentId,
    });
  };

  onConfirmEdit = async (text: string) => {
    const { onEditComment, id, parentId } = this.props;
    try {
      await onEditComment({
        commentId: id,
        parentId,
        text,
      });
      this.setState({
        editing: false,
      });
    } catch (e) {
      console.log(e);
    }
  };

  onReply = (text: string, replyTo?: ?string) => {
    return this.props.onReply(text, replyTo, this.props.parentId);
  };

  render() {
    const {
      username,
      thumbnail,
      comment,
      repliesCount,
      level,
      id,
      onReply,
      replies,
      subCommentsMap,
      onReadReplies,
      date,
      logged,
      currentUsername,
      onOpenRemove,
      onEditComment,
    } = this.props;
    const { open, showInput, editing } = this.state;

    const userProfileLink = `/@${username || ''}`;

    return (
      <div className="BrandComment">
        {editing ? (
          <BrandCommentInput
            showCancel
            onCancel={this.onToggleEdit}
            onWriteComment={this.onConfirmEdit}
            editing
            defaultValue={comment || ''}
          />
        ) : (
          <Fragment>
            <div className="comment-head">
              <Link to={userProfileLink}>
                <img
                  src={thumbnail || 'https://images.cacheby.com/default_thumbnail.png'}
                  alt={username}
                />
              </Link>
              <div className="text-block">
                {username === null ? (
                  <div className="username unknown">알 수 없음</div>
                ) : (
                  <Link to={userProfileLink} className="username">
                    {username}
                  </Link>
                )}
                <div className="date">{fromNow(date)}</div>
              </div>
              {username && username === currentUsername && (
                <div className="actions">
                  <button className="edit" onClick={this.onToggleEdit}>
                    수정
                  </button>
                  <button className="remove" onClick={this.onOpenRemove}>
                    삭제
                  </button>
                </div>
              )}
            </div>
            <div
              className={cx('comment-body', {
                deleted: comment === null,
              })}
            >
              {comment ? <MarkdownRender body={comment} /> : '삭제된 브랜드 댓글입니다.'}
            </div>
          </Fragment>
        )}
        {level < 3 &&
          (open ? (
            <button className="replies-button" onClick={this.onClose}>
              <FaRegMinusSquare />
              숨기기
            </button>
          ) : (
            (logged || repliesCount > 0) &&
            (comment || repliesCount > 0) && (
              <button className="replies-button" onClick={this.onOpen}>
                <FaRegPlusSquare />
                {repliesCount === 0 ? '답변 달기' : `${repliesCount}개의 답변`}
              </button>
            )
          ))}
        {open && (
          <section className="replies">
            {replies &&
              replies.map(reply => {
                return (
                  <BrandComment
                    logged={logged}
                    key={reply.id}
                    id={reply.id}
                    username={reply.user.username}
                    thumbnail={reply.user.thumbnail}
                    comment={reply.text}
                    date={reply.created_at}
                    replies={subCommentsMap[reply.id]}
                    repliesCount={reply.replies_count}
                    subCommentsMap={subCommentsMap}
                    onReadReplies={onReadReplies}
                    onReply={onReply}
                    level={level + 1}
                    currentUsername={currentUsername}
                    onOpenRemove={onOpenRemove}
                    parentId={id}
                    onEditComment={onEditComment}
                  />
                );
              })}
            {showInput ? (
              <BrandCommentInput
                showCancel
                onCancel={this.onHideInput}
                onWriteComment={this.onReply}
                replyTo={id}
              />
            ) : (
              logged && (
                <button className="show-input-button" onClick={this.onShowInput}>
                  답변 등록하기
                </button>
              )
            )}
          </section>
        )}
      </div>
    );
  }
}

export default BrandComment;



// WEBPACK FOOTER //
// ./src/components/brand/BrandComment/BrandComment.js